import { Button } from "@components/common/Button";
import { SearchableSelect } from "@components/common/SearchableSelect";

interface TablePaginationProps {
  idPrefix: string;
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
}

const PAGE_SIZE_OPTIONS = [25, 50, 100].map((size) => ({ id: String(size), label: String(size) }));

export const TablePagination = ({
  idPrefix,
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
}: TablePaginationProps) => {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const rangeStart = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeEnd = Math.min(page * pageSize, total);

  return (
    <div className="flex flex-wrap items-center justify-between gap-4 px-5 py-3">
      {onPageSizeChange ? (
        <div className="flex items-center gap-2 text-sm text-content-secondary dark:text-content-secondary-dark">
          <label htmlFor={`${idPrefix}-page-size`} className="sr-only">Rows per page</label>
          <span>Rows per page</span>
          <SearchableSelect
            id={`${idPrefix}-page-size`}
            aria-label="Rows per page"
            options={PAGE_SIZE_OPTIONS}
            value={String(pageSize)}
            onChange={(value) => onPageSizeChange(Number(value))}
            placeholder="Rows"
            clearable={false}
            className="w-24"
          />
        </div>
      ) : <span />}
      <p className="text-sm text-content-secondary dark:text-content-secondary-dark" aria-live="polite">
        Showing {rangeStart}-{rangeEnd} of {total}
      </p>
      <div className="flex items-center gap-2" aria-label="Pagination">
        <Button variant="secondary" size="sm" disabled={page <= 1} onClick={() => onPageChange(page - 1)}>Previous</Button>
        <span className="text-sm text-content-secondary dark:text-content-secondary-dark" aria-current="page">Page {page} of {totalPages}</span>
        <Button variant="secondary" size="sm" disabled={page >= totalPages} onClick={() => onPageChange(page + 1)}>Next</Button>
      </div>
    </div>
  );
};
